import { useCallback, useEffect, useState } from 'react'
import Cropper from 'react-easy-crop'
import 'react-easy-crop/react-easy-crop.css'
import '../style/imageCropper.css'

/* Cloudflare-varianten som eventsiden henter er "480x380" (24:19).
   Eventsiden viser den i en kasse med h-48 + object-cover, som på desktop
   er ca. 2.5:1 — derfor markerer overlayet en sikkerhedszone i midten. */
export const CROP_WIDTH = 480
export const CROP_HEIGHT = 380
export const CROP_ASPECT = CROP_WIDTH / CROP_HEIGHT

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', () => reject(new Error('Billedet kunne ikke indlæses')))
    image.src = src
  })

const jpegName = (name) => (name || 'billede').replace(/\.[^./\\]+$/, '') + '.jpg'

async function cropToFile(imageSrc, pixelCrop, originalName) {
  const image = await loadImage(imageSrc)
  const canvas = document.createElement('canvas')
  canvas.width = CROP_WIDTH
  canvas.height = CROP_HEIGHT

  const ctx = canvas.getContext('2d')
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    CROP_WIDTH,
    CROP_HEIGHT
  )

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Billedet kunne ikke behandles'))
          return
        }
        resolve(new File([blob], jpegName(originalName), { type: 'image/jpeg' }))
      },
      'image/jpeg',
      0.92
    )
  })
}

/**
 * Beskæringseditor til projektbilleder.
 *
 * @param {File}     file      Den rå fil brugeren har valgt.
 * @param {Function} onConfirm Kaldes med en ny File beskåret til 480x380 JPEG.
 * @param {Function} onCancel  Kaldes hvis brugeren fortryder.
 */
export default function ImageCropper({ file, onConfirm, onCancel }) {
  const [imageSrc, setImageSrc] = useState(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
  const [error, setError] = useState(null)
  const [working, setWorking] = useState(false)

  useEffect(() => {
    if (!file) return undefined
    const url = URL.createObjectURL(file)
    setImageSrc(url)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setError(null)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const onCropComplete = useCallback((_croppedArea, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels)
  }, [])

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return
    setWorking(true)
    setError(null)
    try {
      onConfirm(await cropToFile(imageSrc, croppedAreaPixels, file.name))
    } catch (err) {
      console.error(err)
      setError('Billedet kunne ikke behandles. Prøv en anden fil.')
    }
    setWorking(false)
  }

  if (!imageSrc) return null

  return (
    <div className="mt-2">
      <div className="relative w-full h-64 bg-gray-900 rounded-md overflow-hidden">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={CROP_ASPECT}
          objectFit="auto-cover"
          showGrid={false}
          cropAreaClassName="it-crop-area"
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
        />
      </div>

      <p className="mt-2 text-xs text-gray-500">
        Træk billedet på plads og zoom med skyderen. Det <strong>lyse felt i midten</strong> er
        altid synligt på eventsiden — de mørke bånd i top og bund kan blive klippet væk på
        store skærme. Hold motivet inden for det lyse felt.
      </p>

      <div className="mt-3 flex items-center gap-3">
        <label htmlFor="crop-zoom" className="text-sm text-gray-700 shrink-0">
          Zoom
        </label>
        <input
          id="crop-zoom"
          type="range"
          min={1}
          max={3}
          step={0.01}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          className="w-full"
        />
      </div>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      <div className="mt-3 flex gap-3">
        <button
          type="button"
          onClick={handleConfirm}
          disabled={working || !croppedAreaPixels}
          className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          {working ? 'Behandler…' : 'Brug billedet'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Annuller
        </button>
      </div>
    </div>
  )
}
